import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { DndContext, useDraggable, useDroppable, type DragEndEvent } from '@dnd-kit/core';
import { CalendarDays, ChevronLeft, ChevronRight, Video } from 'lucide-react';
import { toast } from 'sonner';
import { PRIORITY_COLOURS } from '@virtuetasks/types';
import { PageHeader } from '@/app/layout/AppShell';
import { useDragSensors } from '@/lib/dndSensors';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/features/auth';
import { useProject } from '@/features/projects';
import { useTasks, useUpdateTask, type TaskView } from '@/features/tasks/api';
import { FilterBar, useFilters } from '@/features/tasks/FilterBar';
import { useLeaveOverlay } from './api';
import {
  useCalendarEvents,
  useCalendarLayers,
  useSetCalendarLayers,
  type CalendarEventView,
} from '@/features/calendar/api';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * The legend doubles as the filter — V5-03 §3.
 *
 * Separating "what the colours mean" from "what is shown" would give two lists
 * saying the same thing, and the second one would drift.
 */
const LAYERS = [
  { key: 'tasks', label: 'Tasks', swatch: 'var(--accent)' },
  { key: 'leave', label: 'Leave', swatch: 'var(--ok)' },
  { key: 'holidays', label: 'Holidays', swatch: 'var(--warn)' },
  { key: 'meetings', label: 'Meetings', swatch: 'var(--info, #7aa2f7)' },
] as const;

/**
 * Per-source toggles under Meetings — V6-07 §4.
 *
 * A second row rather than more chips in the first: `meetings` is the master
 * switch and these decide which of the visible meetings are drawn, and a flat
 * list of seven chips would make that relationship invisible. They only appear
 * once there is more than one source to choose between, so a workspace with
 * only Calendly still sees exactly what it saw before.
 */
const MEETING_SOURCES = [
  { source: 'calendly', key: 'meetings_calendly', label: 'Calendly' },
  { source: 'google', key: 'meetings_google', label: 'Google' },
  { source: 'microsoft', key: 'meetings_microsoft', label: 'Microsoft' },
] as const;

const timeLabel = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit' });

function monthGrid(anchor: Date): Date[] {
  const first = new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth(), 1));
  const offset = (first.getUTCDay() + 6) % 7; // Monday-first
  const start = new Date(first.getTime() - offset * 86_400_000);
  return Array.from({ length: 42 }, (_, i) => new Date(start.getTime() + i * 86_400_000));
}

const iso = (d: Date) => d.toISOString().slice(0, 10);

function DayCell({
  day,
  anchorMonth,
  tasks,
  leaveNames,
  holidayName,
  meetings,
  onOpen,
}: {
  day: Date;
  anchorMonth: number;
  tasks: TaskView[];
  leaveNames: string[];
  holidayName?: string;
  meetings: CalendarEventView[];
  onOpen: (task: TaskView) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `day:${iso(day)}` });
  const isToday = iso(day) === iso(new Date());
  const inMonth = day.getUTCMonth() === anchorMonth;

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex min-h-[104px] flex-col gap-1 border-b border-r border-[var(--border)] p-1',
        !inMonth && 'opacity-45',
      )}
      style={{ background: isOver ? 'var(--accent-soft)' : undefined }}
    >
      <div className="flex items-center gap-1">
        <span
          className={cn(
            'vt-mono grid size-5 place-items-center rounded-full text-[11px]',
            isToday && 'bg-[var(--accent)] font-semibold text-[var(--accent-contrast)]',
          )}
        >
          {day.getUTCDate()}
        </span>
        {holidayName && (
          <span className="truncate text-[10px] text-[var(--warn)]" title={holidayName}>
            {holidayName}
          </span>
        )}
      </div>

      {leaveNames.length > 0 && (
        <div className="truncate text-[10px] vt-muted" title={`On leave: ${leaveNames.join(', ')}`}>
          🌴 {leaveNames.join(', ')}
        </div>
      )}

      {meetings.map((meeting) => (
        <a
          key={meeting._id}
          href={meeting.meetingUrl ?? undefined}
          target="_blank"
          rel="noreferrer"
          data-testid="calendar-meeting"
          data-source={meeting.source}
          title={[
            meeting.title,
            meeting.inviteeName ?? '',
            meeting.location ?? '',
            // The title already reads "Busy"; this explains why rather than
            // implying something is missing (V6-07 §1).
            meeting.isPrivate ? 'Marked private in the calendar it came from' : '',
          ]
            .filter(Boolean)
            .join(' — ')}
          className={cn(
            'flex items-center gap-1 truncate rounded-[4px] px-1 py-0.5 text-left text-[11px]',
            // Read-only: bookings are managed in Calendly, so there is nothing
            // to drag and nothing here pretends otherwise (§3).
            meeting.status === 'cancelled' && 'line-through opacity-50',
          )}
          style={{ background: 'var(--accent-soft)' }}
        >
          <Video className="size-2.5 shrink-0" aria-hidden />
          <span className="truncate">
            {meeting.isAllDay ? '' : `${timeLabel(meeting.startsAt)} `}
            {meeting.inviteeName ?? meeting.title}
          </span>
        </a>
      ))}

      <div className="flex flex-col gap-0.5">
        {tasks.map((task) => (
          <DraggableChip key={task._id} task={task} onOpen={() => onOpen(task)} />
        ))}
      </div>
    </div>
  );
}

function DraggableChip({ task, onOpen }: { task: TaskView; onOpen: () => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: task._id });
  return (
    <button
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onDoubleClick={onOpen}
      title={`${task.key} — ${task.title}`}
      className="flex items-center gap-1 truncate rounded-[4px] bg-[var(--surface-2)] px-1 py-0.5 text-left text-[11px]"
      style={{ opacity: isDragging ? 0.4 : 1 }}
    >
      <span
        className="size-1.5 shrink-0 rounded-full"
        style={{ background: PRIORITY_COLOURS[task.priority] }}
        aria-hidden
      />
      <span className="truncate">{task.title}</span>
    </button>
  );
}

export function CalendarPage({ scope = 'project' }: { scope?: 'project' | 'mine' }) {
  const { key } = useParams();
  const navigate = useNavigate();
  const filters = useFilters();
  const { workspace } = useAuth();
  const { data: project } = useProject(scope === 'project' ? key : undefined);
  const update = useUpdateTask();
  const [monthOffset, setMonthOffset] = useState(0);

  const anchor = useMemo(() => {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + monthOffset, 1));
  }, [monthOffset]);

  const grid = useMemo(() => monthGrid(anchor), [anchor]);
  const rangeFrom = grid[0];
  const rangeTo = grid[grid.length - 1];

  const { data, isLoading } = useTasks(
    {
      ...filters.toQuery(),
      dueFrom: rangeFrom.toISOString(),
      dueTo: rangeTo.toISOString(),
      limit: 200,
      ...(scope === 'project' && project ? { projectId: project._id } : { assigneeId: 'me' }),
    },
    { enabled: scope === 'mine' || Boolean(project) },
  );
  const { data: leave = [] } = useLeaveOverlay(rangeFrom, rangeTo);
  const { data: layerState } = useCalendarLayers();
  const setLayers = useSetCalendarLayers();
  const { data: meetings = [] } = useCalendarEvents(rangeFrom, rangeTo);

  // Undefined while the preference loads — showing everything is the right
  // guess, because a calendar that starts empty and fills in reads as broken.
  const shown = useMemo(
    () =>
      new Set<string>(
        layerState?.layers ?? [...LAYERS.map((l) => l.key), ...MEETING_SOURCES.map((s) => s.key)],
      ),
    [layerState?.layers],
  );

  /**
   * Which meeting sources are actually present this month.
   *
   * Derived from the data rather than from the connection list: a person who
   * disconnected Google last week still has last week's Google meetings, and a
   * toggle that vanished would leave them on screen with no way to hide them.
   */
  const presentSources = MEETING_SOURCES.filter((entry) =>
    meetings.some((meeting) => meeting.source === entry.source),
  );

  function toggle(key: string) {
    const next = shown.has(key)
      ? [...shown].filter((k) => k !== key)
      : [...shown, key];
    setLayers.mutate(next as never);
  }

  const tasks = (data?.items ?? []) as TaskView[];

  const meetingsByDay = useMemo(() => {
    const map = new Map<string, CalendarEventView[]>();
    for (const meeting of meetings) {
      // The per-source toggle decides inclusion here rather than at render, so
      // a day with only hidden meetings collapses cleanly (V6-07 §4).
      const layer = MEETING_SOURCES.find((entry) => entry.source === meeting.source)?.key;
      if (layer && !shown.has(layer)) continue;
      const day = meeting.startsAt.slice(0, 10);
      map.set(day, [...(map.get(day) ?? []), meeting]);
    }
    return map;
  }, [meetings, shown]);

  const byDay = useMemo(() => {
    const map = new Map<string, TaskView[]>();
    for (const task of tasks) {
      if (!task.dueDate) continue;
      const day = task.dueDate.slice(0, 10);
      map.set(day, [...(map.get(day) ?? []), task]);
    }
    return map;
  }, [tasks]);

  const leaveByDay = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const entry of leave) {
      const start = new Date(entry.startDate);
      const end = new Date(entry.endDate);
      for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
        const day = iso(d);
        map.set(day, [...(map.get(day) ?? []), entry.userName]);
      }
    }
    return map;
  }, [leave]);

  const holidaysByDay = useMemo(
    () => new Map((workspace?.holidays ?? []).map((h) => [h.date.slice(0, 10), h.name])),
    [workspace],
  );

  /** Dragging a card to another day reschedules it (§5-F11). */
  // V10 §7 — the shared sensors, so a keystroke inside an editable element
  // can never lift a draggable. See `lib/dndSensors.ts`.
  const sensors = useDragSensors();
  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || !String(over.id).startsWith('day:')) return;

    const task = tasks.find((t) => t._id === String(active.id));
    if (!task) return;
    const day = String(over.id).slice('day:'.length);
    if (task.dueDate?.slice(0, 10) === day) return;

    update
      .mutateAsync({
        id: task._id,
        version: task.version,
        patch: { dueDate: `${day}T18:29:00.000Z` },
      })
      .then(() => toast.success(`${task.key} moved to ${day}`))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.code === 'STALE_WRITE') {
          toast.error('Someone else changed that task — refreshed');
        } else {
          toast.error(err instanceof ApiError ? err.message : 'Could not reschedule');
        }
      });
  }

  const monthLabel = anchor.toLocaleString('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

  return (
    <>
      <PageHeader
        title={scope === 'mine' ? 'My Calendar' : `${project?.icon ?? ''} Calendar`}
        subtitle={`${monthLabel} · drag a task to reschedule it`}
        actions={
          <>
            <Button variant="ghost" size="icon-sm" aria-label="Previous month" onClick={() => setMonthOffset((m) => m - 1)}>
              <ChevronLeft />
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setMonthOffset(0)}>
              Today
            </Button>
            <Button variant="ghost" size="icon-sm" aria-label="Next month" onClick={() => setMonthOffset((m) => m + 1)}>
              <ChevronRight />
            </Button>
          </>
        }
        toolbar={<FilterBar showGrouping={false} />}
      />

      <div
        className="flex flex-wrap items-center gap-1 px-4 pt-3"
        data-testid="calendar-legend"
        role="group"
        aria-label="Calendar layers"
      >
        {LAYERS.map((layer) => {
          const on = shown.has(layer.key);
          return (
            <button
              key={layer.key}
              type="button"
              onClick={() => toggle(layer.key)}
              aria-pressed={on}
              data-testid={`calendar-layer-${layer.key}`}
              className={cn(
                'flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] transition-colors duration-[120ms]',
                on
                  ? 'border-[var(--border)] text-[var(--text)]'
                  : 'border-transparent text-[var(--text-muted)] line-through opacity-60',
              )}
            >
              <span
                className="size-2 rounded-full"
                style={{ background: layer.swatch, opacity: on ? 1 : 0.4 }}
                aria-hidden
              />
              {layer.label}
            </button>
          );
        })}
      </div>

      {/* V6-07 §4 — per-source toggles, only once there is a choice to make. */}
      {shown.has('meetings') && presentSources.length > 1 && (
        <div
          className="flex flex-wrap items-center gap-1 px-4 pt-1.5"
          data-testid="calendar-meeting-sources"
          role="group"
          aria-label="Meeting sources"
        >
          <span className="text-[11px] vt-muted">Meetings from:</span>
          {presentSources.map((entry) => {
            const on = shown.has(entry.key);
            return (
              <button
                key={entry.key}
                type="button"
                onClick={() => toggle(entry.key)}
                aria-pressed={on}
                data-testid={`calendar-layer-${entry.key}`}
                className={cn(
                  'rounded-full border px-2 py-0.5 text-[11px] transition-colors duration-[120ms]',
                  on
                    ? 'border-[var(--border)] text-[var(--text)]'
                    : 'border-transparent text-[var(--text-muted)] line-through opacity-60',
                )}
              >
                {entry.label}
              </button>
            );
          })}
        </div>
      )}

      <div className="p-4">
        {isLoading ? (
          <Skeleton className="h-[60vh]" />
        ) : (
          <DndContext sensors={sensors} onDragEnd={onDragEnd}>
            <div className="vt-surface overflow-hidden">
              <div className="grid grid-cols-7 border-b border-[var(--border)]">
                {WEEKDAYS.map((day) => (
                  <div
                    key={day}
                    className="border-r border-[var(--border)] px-2 py-1 text-[11px] font-medium uppercase tracking-wide vt-muted last:border-r-0"
                  >
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {grid.map((day) => (
                  <DayCell
                    key={iso(day)}
                    day={day}
                    anchorMonth={anchor.getUTCMonth()}
                    tasks={shown.has('tasks') ? (byDay.get(iso(day)) ?? []) : []}
                    leaveNames={shown.has('leave') ? (leaveByDay.get(iso(day)) ?? []) : []}
                    holidayName={shown.has('holidays') ? holidaysByDay.get(iso(day)) : undefined}
                    meetings={shown.has('meetings') ? (meetingsByDay.get(iso(day)) ?? []) : []}
                    onOpen={(task) => navigate(`/tasks/${task.key}`)}
                  />
                ))}
              </div>
            </div>

            {tasks.length === 0 && (
              <p className="mt-3 flex items-center gap-1.5 text-[12px] vt-muted">
                <CalendarDays className="size-3.5" />
                Nothing is due this month.
              </p>
            )}
          </DndContext>
        )}
      </div>
    </>
  );
}
